/**
 * Which file the Reconciliation screen's *Import 2B* takes, and how (W3-18 (2)).
 *
 * The portal's 2B download is an `.xlsx`, read on the server. The older `.xls` cannot be opened
 * by that reader, and the portal's own 2B JSON is a nested document this screen does not read:
 * the `.json` it takes is ProGST's own rows, an array. Until 2026-10-14 the picker offered `.xls`,
 * and a portal JSON failed somewhere inside the import with nothing said about why. The picker's
 * own `accept` stays a literal on the screen, where `slot-claims.test.ts` holds it to what the
 * service says the 2B slot takes.
 */

/** A file the screen refuses before sending it; its message is written for the person. */
export class TwoBFileError extends Error {}

export type TwoBFile = { kind: 'workbook' } | { kind: 'rows' } | { kind: 'refused'; why: string }

export function twoBFileOf(name: string): TwoBFile {
  if (/\.xlsx$/i.test(name)) return { kind: 'workbook' }
  if (/\.json$/i.test(name)) return { kind: 'rows' }
  if (/\.xls$/i.test(name)) {
    return {
      kind: 'refused',
      why: 'That is an older .xls file. Download the GSTR-2B from the portal as Excel (.xlsx) and import that.',
    }
  }
  return { kind: 'refused', why: "Import the portal's GSTR-2B Excel download (.xlsx)." }
}

/** ProGST's own rows from a `.json`, or a refusal in words when the file is not that. */
export function twoBRowsOf(text: string): Array<Record<string, unknown>> {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new TwoBFileError("That .json file could not be read. Import the portal's GSTR-2B Excel download (.xlsx).")
  }
  if (!Array.isArray(parsed)) {
    throw new TwoBFileError(
      "That .json is not a list of rows — the portal's own 2B JSON is not read here. Import its Excel download (.xlsx).",
    )
  }
  return parsed as Array<Record<string, unknown>>
}
